<template>
  <div v-if="isPropertyListLoading">
    <Loading class="w-12 h-12" />
  </div>
  <div v-else class="w-96 top-4" v-on:keypress="focusSearchBox">
    <Listbox v-model="selectedProperty">
      <div class="relative mt-1">
        <ListboxButton
          class="relative w-full py-2 pl-3 pr-10 text-left cursor-pointer bg-white hover:bg-blue-100 rounded-lg border shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
          :class="selectedProperty?.isActive ? '' : 'bg-gray-300'"
        >
          <div v-if="!selectedProperty">
            -- Select {{ props.IsClientSelectorOnly ? "Client" : "Property" }} --
          </div>
          <div v-else>
            <span v-if="props.IsClientSelectorOnly">
              <div class="text-xs text-gray-600">
                <span> {{ selectedProperty?.clientName }}</span>
              </div>
            </span>
            <span v-else>
              <div class="text-xs text-gray-600">
                <span>
                  {{ selectedProperty?.clientName }} > {{ selectedProperty?.category }} >
                </span>
              </div>
              <div>
                <span class="font-bold text-gray-800 text-md">
                  {{ selectedProperty?.name }}
                  <span v-if="!selectedProperty?.isActive" class="text-xs"> (disabled) </span>
                </span>
              </div>
            </span>
            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <i-heroicons-solid-selector
                class="w-5 h-5 text-2xl text-gray-400"
                aria-hidden="true"
              />
            </span>
          </div>
        </ListboxButton>

        <transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 border border-gray-300 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50"
          >
            <div class="mx-4">
              <input
                placeholder="Search"
                type="text"
                id="searchBox"
                ref="searchBox"
                class="border border-gray-300 w-full mb-2 mt-2 px-2 py-1"
                v-model="searchText"
              />
            </div>
            <div v-for="client in groupedByClient" :key="client.clientName">
              <Disclosure
                v-if="client.properties.length > 1 && !props.IsClientSelectorOnly"
                v-slot="{ open }"
                :default-open="!!client.properties.find((p) => isSelected(p))"
              >
                <DisclosureButton class="w-full">
                  <span
                    class="select-none flex py-1 pl-4 pr-4 cursor-pointer hover:bg-blue-200 w-full text-left"
                  >
                    <i-heroicons-outline-chevron-up
                      :class="open ? '' : 'transform rotate-180'"
                      class="w-5 h-5 grow-0"
                    />
                    <h1 class="grow pl-2">{{ client.clientName }}</h1>
                  </span>
                </DisclosureButton>
                <DisclosurePanel>
                  <ListboxOption
                    v-slot="{ active, selected }"
                    v-for="property in client.properties"
                    :key="property?.name"
                    :value="property"
                    as="template"
                    :class="property?.isActive ? '' : 'bg-gray-300'"
                  >
                    <li
                      class=""
                      :class="[
                        isSelected(property)
                          ? 'border-1 bg-blue-50 border-blue-500 cursor-no-drop'
                          : 'cursor-pointer hover:bg-blue-200',
                        'text-gray-900 select-none relative py-1 pl-4 pr-4 ml-4',
                      ]"
                    >
                      <div class="flex">
                        <i-heroicons-outline-office-building class="w-6 h-6 mt-1" />
                        <span class="pl-2">
                          <div class="text-xs text-gray-600">
                            <span> {{ property?.clientName }} > {{ property?.category }} > </span>
                          </div>
                          <div>
                            <span
                              class="font-bold text-gray-800 text-md"
                              :class="[
                                isSelected(property) ? 'font-medium' : 'font-normal',
                                selected ? 'property-selected' : 'not-selected',
                                active ? 'property-active' : 'not-active',
                                'block truncate',
                              ]"
                            >
                              {{ property?.name }}
                              <span v-if="!property?.isActive" class="text-xs"> (disabled) </span>
                            </span>
                            <span
                              v-if="isSelected(property)"
                              class="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600"
                            >
                              <i-heroicons-outline-check class="w-5 h-5" aria-hidden="false" />
                            </span>
                          </div>
                        </span>
                      </div>
                    </li>
                  </ListboxOption>
                </DisclosurePanel>
              </Disclosure>
              <ListboxOption
                v-else
                v-slot="{ active, selected }"
                :value="client.properties[0]"
                as="template"
              >
                <li
                  class=""
                  :class="[
                    isSelected(client.properties[0])
                      ? 'border-1 bg-blue-50 border-blue-500 cursor-no-drop'
                      : 'cursor-pointer hover:bg-blue-200',
                    'text-gray-900 select-none relative py-1 pl-4 pr-4',
                    client.properties[0]?.isActive ? '' : 'bg-gray-300',
                  ]"
                >
                  <div class="flex">
                    <i-heroicons-outline-office-building class="w-6 h-6 mt-1" />
                    <span class="pl-2">
                      <div class="text-xs text-gray-600">
                        <span>
                          {{ client.properties[0]?.clientName }} >
                          {{ client.properties[0]?.category }} >
                        </span>
                      </div>
                      <div>
                        <span
                          class="font-bold text-gray-800 text-md"
                          :class="[
                            isSelected(client.properties[0]) ? 'font-medium' : 'font-normal',
                            selected ? 'property-selected' : 'not-selected',
                            active ? 'property-active' : 'not-active',
                            'block truncate',
                          ]"
                        >
                          {{ props.IsClientSelectorOnly ? client.clientName : client.properties[0]?.name }}
                          <span v-if="!client.properties[0]?.isActive && !props.IsClientSelectorOnly" class="text-xs">
                            (disabled)
                          </span>
                        </span>
                        <span
                          v-if="isSelected(client.properties[0])"
                          class="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600"
                        >
                          <i-heroicons-outline-check class="w-5 h-5" aria-hidden="false" />
                        </span>
                      </div>
                    </span>
                  </div>
                </li>
              </ListboxOption>
            </div>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>
</template>

<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/vue";
import { useBldgStore } from "../store/bldgStore";
import { PortalDTO } from "../store/generatedFetchClient";

const bldgStore = useBldgStore();
const route = useRoute();

const emit = defineEmits<{
  (e: "selected", val: PortalDTO | null): void;
  (e: "update:modelValue", val: PortalDTO | null): void;
}>();

let props = withDefaults(
  defineProps<{
    IsClientSelectorOnly?: boolean;
    modelValue?: PortalDTO | null;
  }>(),
  {
    IsClientSelectorOnly: false,
    modelValue: undefined,
  }
);

const searchBox = ref<HTMLInputElement | null>(null);
const searchText = ref<string>("");

var isPropertyListLoading = computed(() => bldgStore.isPortalListLoading);
var properties = computed(() =>
  [...bldgStore.properties].sort((a, b) =>
    (a.clientName || '') > (b.clientName || '') ? 1 : (b.clientName || '') > (a.clientName || '') ? -1 : 0
  )
);

var groupedByClient = ref<{ clientName: string; properties: PortalDTO[] }[]>([]);

onMounted(async () => {
  //console.log("Property Selector calling getPropertyList()");
  await bldgStore.getPropertyList();
  populateProperties();
});

watch(properties, populateProperties);
watch(searchText, populateProperties);

function populateProperties() {
  var groups: { clientName: string; properties: PortalDTO[] }[] = [];

  properties.value
    .filter(
      (property) =>
        (property.clientName || '').toLowerCase().includes(searchText.value.toLowerCase()) ||
        (property.category || '').toLowerCase().includes(searchText.value.toLowerCase()) ||
        (property.name || '').toLowerCase().includes(searchText.value.toLowerCase()) ||
        (property.siteCode || '').toLowerCase().includes(searchText.value.toLowerCase())
    )
    .forEach((property) => {
      if (groups.map((group) => group.clientName).includes(property.clientName || '')) {
        groups[
          groups.map((group) => group.clientName).indexOf(property.clientName || '')
        ].properties.push(property);
      } else {
        groups.push({ clientName: property.clientName || '', properties: [property] });
      }
    });

  //console.log(`Property Selector.PopulateProperties`, groups);
  groupedByClient.value = groups;
}

let selectedProperty = computed({
  get: () => props.modelValue ?? null,
  set: (val) => {
    searchText.value = "";
    emit("selected", val);
    emit("update:modelValue", val);
  },
});

watch(properties, () => {
  let siteCode = Array.isArray(route.params.sitecode)
    ? route.params.sitecode[0]
    : route.params.sitecode;

  if (properties.value && siteCode) {
    let selectProp = properties.value.find(
      (p) => p.siteCode?.toLowerCase() === siteCode.toLowerCase()
    );
    if (selectProp) {
      selectedProperty.value = selectProp;
    }
  }
});

function isSelected(portal: PortalDTO) {
  if (selectedProperty?.value?.siteCode === portal?.siteCode) {
    return true;
  }
  return false;
}

function focusSearchBox(event: KeyboardEvent) {
  if (searchBox.value !== null && searchBox.value.id !== document.activeElement?.id) {
    searchBox.value.focus();
  }
}
</script>
